import { authenticatedApiRequest, cleanEmptyFields } from "./core";
import type { PaginatedResponse, ReleaseQueryParams } from "./core";
import type {
  Release,
  ReleaseDetails,
  CreateRelease,
  UpdateRelease,
  Recording,
  CreateReleaseRecording,
} from "@/types";
import type { PaginatedRecordingsResponse } from "./recordings";

export interface PaginatedReleasesResponse extends PaginatedResponse {
  results: Release[];
}

export const releaseApi = {
  getRelease: async (id: string): Promise<Release> => {
    return authenticatedApiRequest<Release>(`/catalogue/releases/${id}`);
  },
  getReleaseDetails: async (id: string): Promise<ReleaseDetails> => {
    return authenticatedApiRequest<ReleaseDetails>(`/catalogue/releases/${id}/details`);
  },
  getReleases: async (filters?: ReleaseQueryParams): Promise<PaginatedReleasesResponse> => {
    const queryFilters = filters
      ? new URLSearchParams(
          Object.entries(filters).filter(([, v]) => v != null) as [string, string][],
        ).toString()
      : "";
    return authenticatedApiRequest<PaginatedReleasesResponse>(
      `/catalogue/releases${queryFilters ? `?${queryFilters}` : ""}`,
    );
  },
  createRelease: async (release: CreateRelease): Promise<Release> => {
    // If artwork is included, use FormData for multipart upload
    if (release.artwork instanceof File) {
      const formData = new FormData();

      // Add the artwork file
      formData.append("artwork", release.artwork);

      // Add other fields to FormData
      Object.entries(cleanEmptyFields(release)).forEach(([key, value]) => {
        if (key !== "artwork" && value !== undefined && value !== null) {
          formData.append(key, String(value));
        }
      });

      return authenticatedApiRequest<Release>("/catalogue/releases", {
        method: "POST",
        body: formData,
      });
    }

    // Regular JSON request if no artwork
    return authenticatedApiRequest<Release>("/catalogue/releases", {
      method: "POST",
      body: JSON.stringify(cleanEmptyFields(release)),
    });
  },
  updateRelease: async (id: string, release: UpdateRelease): Promise<Release> => {
    // If artwork is included, use FormData for multipart upload
    if (release.artwork instanceof File) {
      const formData = new FormData();

      // Add the artwork file
      formData.append("artwork", release.artwork);

      // Add other fields to FormData
      Object.entries(cleanEmptyFields(release)).forEach(([key, value]) => {
        if (key !== "artwork" && value !== undefined && value !== null) {
          formData.append(key, String(value));
        }
      });

      return authenticatedApiRequest<Release>(`/catalogue/releases/${id}`, {
        method: "PUT",
        body: formData,
      });
    }

    // Regular JSON request if no artwork
    return authenticatedApiRequest<Release>(`/catalogue/releases/${id}`, {
      method: "PUT",
      body: JSON.stringify(cleanEmptyFields(release)),
    });
  },
  patchRelease: async (id: string, updates: Partial<UpdateRelease>): Promise<Release> => {
    // PATCH request for partial updates using JSON (without artwork)
    return authenticatedApiRequest<Release>(`/catalogue/releases/${id}`, {
      method: "PATCH",
      body: JSON.stringify(updates),
    });
  },
  patchReleaseArtwork: async (id: string, artwork: File | null): Promise<Release> => {
    // PATCH request with only the artwork field using FormData
    const formData = new FormData();

    if (artwork) {
      formData.append("artwork", artwork);
    } else {
      // Send empty string to signal artwork removal
      formData.append("artwork", "");
    }

    return authenticatedApiRequest<Release>(`/catalogue/releases/${id}`, {
      method: "PATCH",
      body: formData,
      headers: {}, // Let the browser set Content-Type for FormData
    });
  },
  deleteRelease: async (id: string): Promise<void> => {
    await authenticatedApiRequest<void>(`/catalogue/releases/${id}`, {
      method: "DELETE",
    });
  },
  getReleaseRecordings: async (id: string): Promise<PaginatedRecordingsResponse> => {
    return authenticatedApiRequest<PaginatedRecordingsResponse>(
      `/catalogue/releases/${id}/recordings`,
    );
  },
  createRecording: async (release_id: string, recording: CreateReleaseRecording): Promise<Recording> => {
    return authenticatedApiRequest<Recording>(`/catalogue/releases/${release_id}/recordings`, {
      method: "POST",
      body: JSON.stringify(cleanEmptyFields(recording)),
    });
  },
  linkRecordingToRelease: async (
    releaseId: string,
    recordingId: string,
    trackNumber?: string,
    replaceExisting?: boolean,
  ): Promise<Recording> => {
    return authenticatedApiRequest<Recording>(`/catalogue/releases/${releaseId}/recordings`, {
      method: "POST",
      body: JSON.stringify({
        sound_recording_id: recordingId,
        track_number: trackNumber,
        ...(replaceExisting && { replace_existing: true }),
      }),
    });
  },
  unlinkRecordingFromRelease: async (releaseId: string, recordingId: string): Promise<void> => {
    await authenticatedApiRequest<void>(
      `/catalogue/releases/${releaseId}/recordings/${recordingId}`,
      { method: "DELETE" },
    );
  },
  // Add contributors to release with specified roles
  addContributors: async (
    releaseId: string,
    contributors: Array<{ id: string; role: string }>,
  ): Promise<void> => {
    if (contributors.length === 0) return;

    for (const contributor of contributors) {
      await authenticatedApiRequest(`/catalogue/releases/${releaseId}/contributors`, {
        method: "POST",
        body: JSON.stringify({
          contributor: contributor.id,
          role: contributor.role,
        }),
      });
    }
  },
  // Sync contributors to release (replaces existing contributors)
  syncContributors: async (
    releaseId: string,
    contributors: Array<{ contributor: string; role: string }>,
  ): Promise<void> => {
    await authenticatedApiRequest(`/catalogue/releases/${releaseId}/contributors/sync`, {
      method: "PUT",
      body: JSON.stringify({ contributors }),
    });
  },
  // Sync all recordings for a release (adds, removes, updates track numbers atomically)
  syncReleaseRecordings: async (
    releaseId: string,
    recordings: Array<{ sound_recording_id: string; track_number: string | null }>,
  ): Promise<{ linked: number; removed: number; updated: number }> => {
    return authenticatedApiRequest<{ linked: number; removed: number; updated: number }>(
      `/catalogue/releases/${releaseId}/recordings/sync`,
      {
        method: "POST",
        body: JSON.stringify({ recordings }),
      },
    );
  },
};
